import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Database, Upload, RefreshCw, CheckCircle, XCircle, TrendingUp } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card } from '../components/ui/card';
import { Textarea } from '../components/ui/textarea';
import { useTheme } from '../context/ThemeContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminDashboard() {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [ingestionStats, setIngestionStats] = useState(null);
  const [tournamentUrl, setTournamentUrl] = useState('');
  const [bulkUrls, setBulkUrls] = useState('');
  const [ingesting, setIngesting] = useState(false);
  const [idMappings, setIdMappings] = useState([]);
  const [duplicates, setDuplicates] = useState([]);

  useEffect(() => {
    fetchIngestionStats();
  }, []);

  const fetchIngestionStats = async () => {
    try {
      const response = await axios.get(`${API}/ingest/stats`);
      setIngestionStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const ingestSingleTournament = async () => {
    if (!tournamentUrl.trim()) {
      toast.error('Please enter a tournament URL');
      return;
    }

    setIngesting(true);
    try {
      const response = await axios.post(`${API}/ingest/cricclubs/tournament`, {
        tournament_url: tournamentUrl,
        auto_ingest_matches: true
      });

      toast.success(`Tournament ingested! ${response.data.teams_ingested} teams, ${response.data.matches_ingested} matches`);
      setTournamentUrl('');
      fetchIngestionStats();
    } catch (error) {
      toast.error('Ingestion failed: ' + (error.response?.data?.detail || error.message));
    } finally {
      setIngesting(false);
    }
  };

  const ingestBulkTournaments = async () => {
    const urls = bulkUrls.split('\n').filter(url => url.trim());
    
    if (urls.length === 0) {
      toast.error('Please enter at least one URL');
      return;
    }

    try {
      const response = await axios.post(`${API}/ingest/cricclubs/bulk`, {
        tournament_urls: urls
      });

      toast.success(`Bulk ingestion started for ${urls.length} tournaments`);
      setBulkUrls('');
    } catch (error) {
      toast.error('Bulk ingestion failed');
    }
  };

  const checkDuplicates = async () => {
    try {
      const response = await axios.get(`${API}/admin/duplicate-check`);
      setDuplicates(response.data.duplicates);
      toast.info(`Found ${response.data.duplicates_found} potential duplicates`);
    } catch (error) {
      toast.error('Duplicate check failed');
    }
  };

  const fetchIdMappings = async () => {
    try {
      const response = await axios.get(`${API}/admin/id-mappings?limit=50`);
      setIdMappings(response.data.mappings);
    } catch (error) {
      toast.error('Failed to fetch ID mappings');
    }
  };

  return (
    <div className="min-h-screen" style={{ 
      background: theme === 'dark' 
        ? 'linear-gradient(135deg, #0a0e27 0%, #1a1f3a 100%)' 
        : 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)'
    }}>
      <div className={`backdrop-blur-md ${theme === 'dark' ? 'bg-white/5' : 'bg-black/5'} border-b ${theme === 'dark' ? 'border-white/10' : 'border-black/10'}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <button 
            onClick={() => navigate('/')} 
            className={`flex items-center ${theme === 'dark' ? 'text-gray-400 hover:text-white' : 'text-gray-600 hover:text-gray-900'} transition-colors mb-4`}
            data-testid="back-button"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            Back to Home
          </button>
          <h1 className={`text-4xl font-bold ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
            Data Ingestion Admin
          </h1>
          <p className={`${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'} mt-2`}>
            Manage CricClubs data with intelligent ID mapping & deduplication
          </p>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          <div className="lg:col-span-3">
            <h2 className={`text-2xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Ingestion Statistics
            </h2>
            {ingestionStats && (
              <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
                  <div className="text-3xl font-bold gradient-text">{ingestionStats.total_players}</div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Players</div>
                </Card>
                <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
                  <div className="text-3xl font-bold gradient-text">{ingestionStats.total_matches}</div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Matches</div>
                </Card>
                <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
                  <div className="text-3xl font-bold gradient-text">{ingestionStats.total_tournaments}</div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Tournaments</div>
                </Card>
                <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
                  <div className="text-3xl font-bold gradient-text">{ingestionStats.total_teams}</div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>Teams</div>
                </Card>
                <Card className={`glass-card text-center ${theme === 'light' ? 'bg-white/95' : ''}`}>
                  <div className="text-3xl font-bold text-green-500">{ingestionStats.cricclubs_sourced_players}</div>
                  <div className={`text-sm ${theme === 'dark' ? 'text-gray-400' : 'text-gray-600'}`}>CricClubs</div>
                </Card>
              </div>
            )}
          </div>

          <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <h3 className={`text-xl font-bold mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              <Upload className="w-5 h-5 mr-2 text-green-500" />
              Ingest Tournament
            </h3>
            
            <div className="space-y-4">
              <Input
                type="text"
                placeholder="https://cricclubs.com/tournament/..."
                value={tournamentUrl}
                onChange={(e) => setTournamentUrl(e.target.value)}
                className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
              />
              
              <Button 
                onClick={ingestSingleTournament}
                disabled={ingesting}
                className="w-full bg-gradient-to-r from-green-500 to-emerald-600"
              >
                {ingesting ? 'Ingesting...' : 'Ingest Tournament'}
              </Button>
            </div>
          </Card>

          <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <h3 className={`text-xl font-bold mb-4 flex items-center ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              <TrendingUp className="w-5 h-5 mr-2 text-purple-500" />
              Bulk Ingestion
            </h3>
            
            <div className="space-y-4">
              <Textarea
                placeholder="URLs (one per line)"
                value={bulkUrls}
                onChange={(e) => setBulkUrls(e.target.value)}
                rows={6}
                className={theme === 'dark' ? 'bg-white/10 border-white/20 text-white' : 'bg-white border-gray-300 text-gray-900'}
              />
              
              <Button 
                onClick={ingestBulkTournaments}
                className="w-full bg-gradient-to-r from-purple-500 to-pink-500"
              >
                Start Bulk Ingestion
              </Button>
            </div>
          </Card>

          <Card className={`glass-card ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Admin Tools
            </h3>
            
            <div className="space-y-3">
              <Button onClick={checkDuplicates} variant="outline" className="w-full">
                Check Duplicates
              </Button>
              <Button onClick={fetchIdMappings} variant="outline" className="w-full">
                View ID Mappings
              </Button>
              <Button onClick={fetchIngestionStats} variant="outline" className="w-full">
                Refresh Stats
              </Button>
            </div>
          </Card>
        </div>

        {ingestionStats?.recent_ingestions && ingestionStats.recent_ingestions.length > 0 && (
          <Card className={`glass-card mt-8 ${theme === 'light' ? 'bg-white/95' : ''}`}>
            <h3 className={`text-xl font-bold mb-4 ${theme === 'dark' ? 'text-white' : 'text-gray-900'}`}>
              Recent Ingestions
            </h3>
            <div className="space-y-2">
              {ingestionStats.recent_ingestions.map((log, idx) => (
                <div key={idx} className={`p-3 rounded-lg ${theme === 'dark' ? 'bg-white/5' : 'bg-gray-100'}`}>
                  <div className="flex justify-between">
                    <span className={theme === 'dark' ? 'text-white' : 'text-gray-900'}>{log.entity_type}</span>
                    <span className={`text-xs ${theme === 'dark' ? 'text-gray-500' : 'text-gray-600'}`}>
                      {new Date(log.timestamp).toLocaleString()}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
    </div>
  );
}